import * as React from "react";

import {compose} from "redux";
import {connect} from "react-redux";
import {translate} from "react-i18next";
import {t} from "i18next";
import {Icon} from "semantic-ui-react";

import Dialog from "../../../components/Dialogs/Dialog";
import Button from "../../../components/Button";
import {
    OptionDto,
    OptionWorkflowStatusMultiUpdateParam,
    OptionWorkflowStatusMultiUpdateParamActionType,
    RoleDto,
    WorkflowStatusDtoType
} from "../../../services/Client";

import DataAdapterOptionsView from "../DataAdapterOptionsView";

import {EStepTab, StepHeader, stepTabList} from "./WSMUUtils";
import {WSMUActionSelection} from "./WSMUActionSelection";
import {WSMUSummary} from "./WSMUSummary";
import {SelectType} from "./WSMUSummaryUtils";
import {WSMUConfirmDialog} from "./WSMUConfirmDialog";
import {selectUserRoles} from "../../../redux/selectors";
import {IReduxState} from "src/redux/IReduxState";

interface IMapStateToProps {
    userRoles: RoleDto[];
}

interface IOwnProps {
    // selected items
    items: DataAdapterOptionsView[];
    workflowStatusMultiUpdateErrorResult?: number;

    // actions
    onSubmit: (param: OptionWorkflowStatusMultiUpdateParam) => void;
    onClose: () => void;
    onRefresh: () => void;
}

interface IWorkflowStatusMultiUpdateDialogProps extends IMapStateToProps, IOwnProps { }

interface IWorkflowStatusMultiUpdateDialogState {
    activeTab: number;
    actionType?: OptionWorkflowStatusMultiUpdateParamActionType;
    itemsToUpdate: Map<number, OptionDto>;
    loaderActive: boolean;
}

class WorkflowStatusMultiUpdateDialog extends
    React.Component<IWorkflowStatusMultiUpdateDialogProps,
    IWorkflowStatusMultiUpdateDialogState> {
    constructor(props: IWorkflowStatusMultiUpdateDialogProps) {
        super(props);

        this.state = {
            activeTab: EStepTab.ActionTab,
            loaderActive: false,
            itemsToUpdate: new Map()
        };
    }

    componentDidUpdate(prevProps: Readonly<IWorkflowStatusMultiUpdateDialogProps>): void {
        if (this.props.workflowStatusMultiUpdateErrorResult !== undefined &&
            prevProps.workflowStatusMultiUpdateErrorResult !== this.props.workflowStatusMultiUpdateErrorResult) {
            this.setState({
                activeTab: EStepTab.ResultTab,
                loaderActive: false
            });
        }
    }

    private checkUserRightsForCurrentStep = (item: DataAdapterOptionsView): boolean => {
        const currentStep = item.statusValueState && item.statusValueStates ?
            item.statusValueStates.find((element) => element.id === item.statusValueState) : undefined;

        return currentStep !== undefined &&
            this.props.userRoles.find((role) => role.name === currentStep.role) !== undefined;
    }

    private getInitialItemsForReset = (): DataAdapterOptionsView[] => {
        return this.props.items.filter((item) =>
            item.statusValueStateResetable === true
        );
    }

    private getInitialItemsForMoveForward = (): DataAdapterOptionsView[] => {
        return this.props.items.filter((item) =>
            item.statusView !== WorkflowStatusDtoType.APPROVAL &&
            item.statusView !== WorkflowStatusDtoType.APPROVED &&
            this.checkUserRightsForCurrentStep(item)
        );
    }

    private getValidItemsBasedOnActionType = (): DataAdapterOptionsView[] => {
        return this.state.actionType === OptionWorkflowStatusMultiUpdateParamActionType.RESET ? this.getInitialItemsForReset() :
            this.state.actionType === OptionWorkflowStatusMultiUpdateParamActionType.MOVE_FORWARD ? this.getInitialItemsForMoveForward() :
                [];
    }

    private mapInitialItems = () => {
        let map = new Map();
        const validItems = this.getValidItemsBasedOnActionType();

        validItems.forEach((item) => {
            map.set(item.dto.objectId, item.dto);
        });

        return map;
    }

    private handleSelectChange = (selectType: SelectType, optionDto?: OptionDto) => {
        let {itemsToUpdate} = this.state;

        switch (selectType) {
            case SelectType.SelectAll:
                itemsToUpdate = this.mapInitialItems();
                break;
            case SelectType.SingleSelect:
                if (optionDto) {
                    if (itemsToUpdate.has(optionDto.objectId)) {
                        itemsToUpdate.delete(optionDto.objectId);
                    } else {
                        itemsToUpdate.set(optionDto.objectId, optionDto);
                    }
                }
                break;
            default:
                itemsToUpdate = new Map();
                break;
        }

        this.setState({itemsToUpdate});
    }

    private onSubmit = () => {
        if (this.state.actionType) {

            this.setState({loaderActive: true});

            const param: OptionWorkflowStatusMultiUpdateParam = {
                optionDtoList: Array.from(this.state.itemsToUpdate.values()),
                actionType: this.state.actionType
            };

            this.props.onSubmit(param);
        }
    }

    render() {
        const dlgButton = this.state.activeTab === EStepTab.ResultTab
            ? (
                <Button
                    key={"refreshButton"}
                    icon={"refresh"}
                    onClick={this.props.onRefresh}
                    disabled={this.state.loaderActive}
                />
            )
            : (
                <Button
                    key={"cancelButton"}
                    icon={"ban"}
                    onClick={this.props.onClose}
                    disabled={this.state.loaderActive}
                />
            );

        const validItems = this.getValidItemsBasedOnActionType();
        const invalidItems = this.props.items.filter((item) => validItems.findIndex((uI) => uI.dto.objectId === item.dto.objectId) === -1);

        return (
            <Dialog
                open={true}
                title={t("PSL_WF_S_MULTI:UPDATE_WF_STATUS_DLG_TITLE")}
                customStyleClassName={'multi-step-dialog'}
                actions={[dlgButton]}
            >
                <div style={{marginBottom: '30px'}}>
                    <StepHeader
                        activeTab={this.state.activeTab}
                        steps={stepTabList}
                        t={t}
                    />
                </div>

                {/** First Tab Start */}
                {this.state.activeTab === EStepTab.ActionTab &&
                    <WSMUActionSelection
                        value={this.state.actionType}
                        t={t}
                        onChange={(actionType) => this.setState({actionType})}
                        nextAction={() =>
                            this.setState({
                                activeTab: this.state.activeTab + 1,
                                itemsToUpdate: this.mapInitialItems()
                            })
                        }
                    />}
                {/** First Tab End */}

                {/** Second Tab Start */}
                {this.state.activeTab === EStepTab.SummaryTab && this.state.actionType &&
                    <WSMUSummary
                        validItems={validItems}
                        invalidItems={invalidItems}
                        actionType={this.state.actionType}
                        itemsToUpdate={this.state.itemsToUpdate}
                        t={t}
                        handleSelectChange={this.handleSelectChange}
                        backAction={() => this.setState({activeTab: this.state.activeTab - 1})}
                        nextAction={() => this.setState({activeTab: this.state.activeTab + 1})}
                    />}
                {/** Second Tab End */}

                {/** Third Tab Start */}
                {this.state.activeTab === EStepTab.ConfirmTab && this.state.actionType && this.state.itemsToUpdate.size > 0 &&
                    <WSMUConfirmDialog
                        loaderActive={this.state.loaderActive}
                        actionType={this.state.actionType}
                        approvedItemsCount={this.getValidItemsBasedOnActionType().filter((it) =>
                            this.state.itemsToUpdate.has(it.objectId) && it.statusView === WorkflowStatusDtoType.APPROVED).length}
                        itemsToUpdateCount={this.state.itemsToUpdate.size}
                        t={t}
                        onSubmit={this.onSubmit}
                        backAction={() => this.setState({activeTab: this.state.activeTab - 1})}
                    />}
                {/** Third Tab End */}

                {/** Fourth Tab Start */}
                <>
                    {this.state.activeTab === EStepTab.ResultTab && this.props.workflowStatusMultiUpdateErrorResult !== undefined &&
                        <>
                            <p>
                                <b>{this.state.itemsToUpdate.size - this.props.workflowStatusMultiUpdateErrorResult}</b>
                                {' ' + t("PSL_WF_S_MULTI:RESULT_SUCCESS")}
                            </p>
                            {this.props.workflowStatusMultiUpdateErrorResult > 0 &&
                                <div className={"display--flex"}>
                                    <Icon name="exclamation triangle" color="red" />
                                    <p>
                                        <b style={{color: 'red'}}>{this.props.workflowStatusMultiUpdateErrorResult}</b>
                                        {' ' + t("PSL_WF_S_MULTI:RESULT_ERROR")}
                                        {' ' + t("PSL_WF_S_MULTI:RESULT_ERROR_DISCLAIMER")}
                                    </p>
                                </div>
                            }
                        </>
                    }
                </>
                {/** Fourth Tab End */}
            </Dialog>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    userRoles: selectUserRoles(state) || [],
});

export default compose<React.ComponentType<IOwnProps>>(
    translate("PSL_WF_S_MULTI"),
    connect<IMapStateToProps>(mapStateToProps))
    (WorkflowStatusMultiUpdateDialog);



// WEBPACK FOOTER //
// ./src/views/OptionsView/workflowStatusMultiUpdate/WorkflowStatusMultiUpdateDialog.tsx